import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountingAPI } from '../services/api';
import { ArrowLeft, Save, DollarSign, Receipt, Plus, X } from 'lucide-react';

const EXPENSE_CATEGORIES = [
  'Rent',
  'Utilities',
  'Insurance',
  'Marketing',
  'Office Supplies',
  'Travel',
  'Meals & Entertainment',
  'Professional Fees',
  'Repairs & Maintenance',
  'Shipping & Postage',
  'Software & Subscriptions',
  'Taxes & Licenses',
  'Payroll',
  'Bank Fees',
  'Other'
];

const PAYMENT_METHODS = [
  'Cash',
  'Check',
  'Credit Card',
  'Debit Card',
  'Wire Transfer',
  'ACH'
];

const RecordExpensePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [newVendor, setNewVendor] = useState({
    vendor_name: '',
    email: '',
    phone: '',
    tax_id: '',
    vendor_type: 'Individual',
    tax_id_type: 'SSN'
  });
  const [formData, setFormData] = useState({
    vendor_id: '',
    category: '',
    amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    payment_method: 'Credit Card',
    description: '',
    receipt_number: '',
    is_recurring: false,
    is_paid: true,  // Default to paid since most expenses are recorded after payment
    is_deductible: true,  // Default to tax deductible
    notes: ''
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await accountingAPI.getVendors();
      setVendors(response.data || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCreateVendor = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await accountingAPI.createVendor(newVendor);
      await fetchVendors();
      setFormData(prev => ({ ...prev, vendor_id: response.data.id }));
      setShowVendorModal(false);
      setNewVendor({
        vendor_name: '',
        email: '',
        phone: '',
        tax_id: '',
        vendor_type: 'Individual',
        tax_id_type: 'SSN'
      });
      setSuccessMessage('Vendor created successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error creating vendor:', err);
      setError('Failed to create vendor. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await accountingAPI.createExpense({
        ...formData,
        amount: parseFloat(formData.amount),
        vendor_id: formData.vendor_id ? parseInt(formData.vendor_id) : null
      });

      navigate('/accounting');
    } catch (err) {
      console.error('Error recording expense:', err);
      setError(err.response?.data?.detail || 'Failed to record expense. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/accounting')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Accounting
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Record Expense</h1>
          <p className="text-gray-600">Track business expenses and automatically create journal entries</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-800">
            <p className="font-medium">{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-xl text-green-800">
            <p className="font-medium">{successMessage}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8">
          <div className="space-y-6">
            {/* Expense Category */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Expense Category *
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              >
                <option value="">Select category...</option>
                {EXPENSE_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Vendor */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Vendor (Optional)
                </label>
                <button
                  type="button"
                  onClick={() => setShowVendorModal(true)}
                  className="flex items-center gap-1 px-3 py-1 text-sm bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add New Vendor
                </button>
              </div>
              <select
                name="vendor_id"
                value={formData.vendor_id}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              >
                <option value="">Select vendor...</option>
                {vendors.map(vendor => (
                  <option key={vendor.id} value={vendor.id}>{vendor.vendor_name || vendor.name}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Select a vendor or leave blank for general expenses
              </p>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Amount *
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0"
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Expense Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Expense Date *
              </label>
              <input
                type="date"
                name="expense_date"
                value={formData.expense_date}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              />
            </div>

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Payment Method *
              </label>
              <select
                name="payment_method"
                value={formData.payment_method}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              >
                {PAYMENT_METHODS.map(method => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description *
              </label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="Brief description of the expense"
              />
            </div>

            {/* Receipt Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Receipt/Invoice Number
              </label>
              <input
                type="text"
                name="receipt_number"
                value={formData.receipt_number}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="Receipt or invoice reference number"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="Any additional details about this expense"
              />
            </div>

            {/* Recurring Expense Checkbox */}
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl">
              <input
                type="checkbox"
                name="is_recurring"
                checked={formData.is_recurring}
                onChange={handleChange}
                className="w-5 h-5 text-blue-600"
              />
              <label className="text-sm font-medium text-gray-700">
                This is a recurring expense (monthly)
              </label>
            </div>

            {/* Accounting Notice */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="text-sm text-blue-800">
                <strong>ℹ️ Note:</strong> This expense will be recorded using accrual accounting principles. A journal entry will be automatically created with proper debits and credits. For tax and financial decisions, always consult a licensed CPA.
              </p>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4 mt-8">
            <button
              type="button"
              onClick={() => navigate('/accounting')}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-2xl font-semibold hover:bg-gray-300 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-2xl font-semibold shadow-lg hover:from-red-700 hover:to-red-600 transition-all disabled:opacity-50"
            >
              <Receipt className="w-5 h-5" />
              {loading ? 'Recording...' : 'Record Expense'}
            </button>
          </div>
        </form>
      </div>

      {/* Add Vendor Modal */}
      {showVendorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Add New Vendor</h2>
              <button
                onClick={() => setShowVendorModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateVendor} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Vendor Name *
                </label>
                <input
                  type="text"
                  value={newVendor.vendor_name}
                  onChange={(e) => setNewVendor({...newVendor, vendor_name: e.target.value})}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder="e.g., Office Supply Co."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={newVendor.email}
                  onChange={(e) => setNewVendor({...newVendor, email: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder="vendor@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={newVendor.phone}
                  onChange={(e) => setNewVendor({...newVendor, phone: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Vendor Type *
                  </label>
                  <select
                    value={newVendor.vendor_type}
                    onChange={(e) => setNewVendor({...newVendor, vendor_type: e.target.value})}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  >
                    <option value="Individual">Individual</option>
                    <option value="LLC (Single-member)">LLC (Single-member)</option>
                    <option value="LLC (Multi-member)">LLC (Multi-member)</option>
                    <option value="Partnership">Partnership</option>
                    <option value="S Corporation">S Corporation</option>
                    <option value="C Corporation">C Corporation</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tax ID Type *
                  </label>
                  <select
                    value={newVendor.tax_id_type}
                    onChange={(e) => setNewVendor({...newVendor, tax_id_type: e.target.value})}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  >
                    <option value="SSN">SSN</option>
                    <option value="EIN">EIN</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tax ID *
                </label>
                <input
                  type="text"
                  value={newVendor.tax_id}
                  onChange={(e) => setNewVendor({...newVendor, tax_id: e.target.value})}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder={newVendor.tax_id_type === 'SSN' ? 'XXX-XX-XXXX' : 'XX-XXXXXXX'}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Required for 1099 reporting (stored securely)
                </p>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowVendorModal(false)}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Create Vendor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordExpensePage;
